import { onScopeDispose } from 'vue';

import { useAppNotifications } from './appNotifications';
import type {
  AppBlockerInput,
  AppNotificationAction,
  AppNotifications,
  NotificationHandle
} from './appNotificationTypes';

type OwnedBlocker = {
  generation: number;
  handle: NotificationHandle;
  notificationKey: string;
};

type OwnedBlockerNotificationsOptions = {
  notifications?: Pick<AppNotifications, 'blocker'>;
};

export const createOwnedBlockerNotifications = (
  notifications: Pick<AppNotifications, 'blocker'>
) => {
  const ownedBlockers = new Map<string, OwnedBlocker>();
  let nextGeneration = 0;

  const release = (ownerKey: string, generation: number) => {
    if (ownedBlockers.get(ownerKey)?.generation === generation) {
      ownedBlockers.delete(ownerKey);
    }
  };

  const clear = (ownerKey: string) => {
    const owned = ownedBlockers.get(ownerKey);
    if (!owned) return;

    ownedBlockers.delete(ownerKey);
    owned.handle.clear();
  };

  const wrapAction = (
    ownerKey: string,
    generation: number,
    action: AppNotificationAction
  ): AppNotificationAction => ({
    ...action,
    shouldClose: () => {
      if (ownedBlockers.get(ownerKey)?.generation !== generation) {
        return false;
      }

      const shouldClose = action.shouldClose?.() ?? true;
      if (shouldClose) {
        release(ownerKey, generation);
      }
      return shouldClose;
    }
  });

  const show = (ownerKey: string, input: AppBlockerInput) => {
    const notificationKey = input.key.trim();
    const previous = ownedBlockers.get(ownerKey);
    if (previous && previous.notificationKey !== notificationKey) {
      clear(ownerKey);
    }

    const generation = ++nextGeneration;
    const handle = notifications.blocker({
      ...input,
      key: notificationKey,
      ...(input.action
        ? { action: wrapAction(ownerKey, generation, input.action) }
        : {})
    });
    ownedBlockers.set(ownerKey, {
      generation,
      handle,
      notificationKey
    });
    return handle;
  };

  const clearAll = () => {
    const handles = Array.from(ownedBlockers.values(), ({ handle }) => handle);
    ownedBlockers.clear();
    for (const handle of handles) {
      handle.clear();
    }
  };

  return {
    clear,
    clearAll,
    show
  };
};

export function useOwnedBlockerNotifications(
  options: OwnedBlockerNotificationsOptions = {}
) {
  const ownedBlockers = createOwnedBlockerNotifications(
    options.notifications ?? useAppNotifications()
  );
  onScopeDispose(ownedBlockers.clearAll, true);
  return ownedBlockers;
}
